import React, { useEffect, useState } from 'react';
import { Modal, Typography, Table, Tabs, List, Button, Input, notification } from 'antd';
import { PlusOutlined, MinusOutlined, CloseOutlined, CheckOutlined, EditOutlined, AlignLeftOutlined } from '@ant-design/icons';
import "../../css/generatedpools.css";
import { Footer } from 'antd/es/layout/layout';
import cbRedIcon from "../../assets/icons/pooling/cb-red.svg";
import cbGreenIcon from "../../assets/icons/pooling/cb-green.svg";
import infoIcon from "../../assets/icons/auto-pooling/info.svg";
import tokenCubeIcon from "../../assets/icons/auto-pooling/tokenCube.svg";
import vectorrightIcon from "../../assets/icons/auto-pooling/rightvector.svg";
import { generateAutoPoolData, getFinalPoolDetails, updateAutoPoolsData, updatePriceBreakDown } from "../../features/AutoPooling/AutoPoolingSlice";
import ConfirmationModal from "./ConfirmationModal";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

notification.config({
  zIndex: 1500,
});

const addThousandSeparator = (value) => {
  const number = parseFloat(value);

  if (isNaN(number)) {
    return '';
  }

  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};


const removeLastDictionary = (AutoPoolsData) => {
  return AutoPoolsData.length > 0 ? AutoPoolsData.slice(0, -1) : AutoPoolsData;
};

const getLastDictionary = (autoPoolsData) => {
  return autoPoolsData.length > 0 ? autoPoolsData[autoPoolsData.length - 1] : null;
};

const GeneratedPools = ({ generatedPoolResult, isModalVisible, handleCancel, activePoolIndex, setActivePoolIndex, setIsModalVisible }) => {
  const [filterType, setFilterType] = useState('all'); // State to manage filter
  const [isFinalModalVisible, setFinalModalVisible] = useState(false);
  const { AutoPoolsDataPayload, AutoPoolsData } = useSelector((state) => state.autoPooling);
  const [selectedPool, setSelectedPool] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [contributionMap, setContributionMap] = useState({});
  const [hasEdited, setHasEdited] = useState(false);
  const [localValue, setLocalValue] = useState(null);
  const [localInput, setLocalInput] = useState({});

  useEffect(() => {
    const data = filterVessels(filterType, removeLastDictionary(AutoPoolsData));

    const firstcontributionmap = {};

    data.forEach((item) => {
      firstcontributionmap[item.token_id] =
        item.cb_wants_to_sell >= 0 ? Math.trunc(parseFloat(item.cb_consumed)).toFixed(6) : item.cb_wants_to_sell.toFixed(6);
    });

    setContributionMap(firstcontributionmap);
  }, [AutoPoolsData]);


  const handleContributionChange = (inputVal, record) => {
    // Save the typed string directly
    setLocalInput((prev) => ({
      ...prev,
      [record.token_id]: inputVal,
    }));

    const numericVal = parseFloat(inputVal);

    if (!isNaN(numericVal) && (numericVal * 10 ** 6 <= record.cb_wants_to_sell)) {
      setContributionMap((prev) => ({
        ...prev,
        [record.token_id]: numericVal * 10 ** 6,
      }));
    }
  };




  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [scrollHeight, setScrollHeight] = useState(
    window.innerWidth < 1600 ? `${Math.min(window.innerHeight * 0.48 - 30, window.innerHeight - 30)}px` : "60vh"
  );
  useEffect(() => {
    const handleResize = () => {
      setScrollHeight(
        window.innerWidth < 1600 ? `${Math.min(window.innerHeight * 0.48 - 30, window.innerHeight - 30)}px` : "60vh"
      );
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  useEffect(() => {
    setActivePoolIndex(0);
  }, []);

  useEffect(() => {
    setSelectedPool(AutoPoolsDataPayload[activePoolIndex]);
  }, [activePoolIndex, isModalVisible]);

  useEffect(() => {
    if (selectedPool && selectedPool !== null) {
      dispatch(generateAutoPoolData(selectedPool));
    }
  }, [selectedPool, AutoPoolsDataPayload]);


  const handleCloseModal = () => {
    setIsModalVisible(false);
    setFinalModalVisible(false);
    window.location.reload();
  }
  const handleConfirm = () => {
    setFinalModalVisible(false);
    navigate("/poolingHistory");
  };

  const handleTabChange = (key) => {
    setActivePoolIndex(parseInt(key) - 1);
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
  };

  const filterVessels = (filterType, pool) => {
    const poolArray = Array.isArray(pool) ? pool : Object.values(pool);
    if (filterType === "all") {
      return poolArray;
    } else {
      return poolArray.filter((vessel) => vessel.type == filterType);
    }
  };

  const columns = [
    {
      title: 'Token ID',
      dataIndex: 'token_id',
      key: 'token_id',
      align: "center",
      width: 200,
      render: (text) => (
        <div className="generated-pool-token vessel-value">
          <img src={tokenCubeIcon} alt="Token Cube" /> {text}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      align: "center",
      render: (text) => (
        <span style={{ color: text === 'deficit' ? '#FB2F2F' : '#0CC876' }}>{text?.charAt(0).toUpperCase() + text?.slice(1)}</span>
      ),
    },
    {
      title: <>Compliance Balance <br /> (CB)</>,
      dataIndex: 'cb_wants_to_sell',
      key: 'cb_wants_to_sell',
      align: "center",
      width: 190,
      render: (value) => {
        const formattedValue = parseFloat(
          (Math.trunc(parseFloat(value / 10 ** 6) * 10 ** 6) / 10 ** 6)?.toFixed(6)
        );
        return (
          <div className="vessel-value" style={{ color: formattedValue < 0 ? "#FB2F2F" : "#0CC876", justifyContent: "center" }}>
            {/* <img width={25} src={formattedValue < 0 ? cbRedIcon : cbGreenIcon} alt="CB Icon" /> */}
            {Math.abs(formattedValue)?.toFixed(6)}
          </div>
        );
      },
    },
    {
      title: <>Contribution <br /> (CB)</>,
      dataIndex: 'cb_consumed',
      key: "cb_consumed",
      align: "center",
      render: (_, record) => {
        const isEditable = editingRow === record.token_id;
        const value =
          contributionMap[record.token_id] ?? Math.min(record?.cb_wants_to_sell >= 0 ? record?.cb_wants_to_sell : record?.cb_consumed, 0);

        const formattedValue = (Math.trunc(value / 10 ** 6 * 10 ** 6) / 10 ** 6);

        if (record.cb_wants_to_sell < 0) return (<div className="vessel-value" style={{ color: formattedValue < 0 ? "#FB2F2F" : "#0CC876", justifyContent: "center" }}>
          {/* <img width={25} src={formattedValue < 0 ? cbRedIcon : cbGreenIcon} alt="CB Icon" /> */}
          {Math.abs(formattedValue)?.toFixed(6)}
        </div>);

        return (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            {isEditable ? (
              <>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={localInput[record.token_id] ?? ""}
                  style={{ width: 100 }}
                  onChange={(e) =>
                    handleContributionChange(e.target.value, record)
                  }
                />
                <Button
                  icon={<CheckOutlined />}
                  size="small"
                  onClick={() => setEditingRow(null)}
                />
              </>
            ) : (
              <>
                <div className="vessel-value" style={{ color: formattedValue < 0 ? "#FB2F2F" : "#0CC876" }}>
                  {/* <img width={25} src={formattedValue < 0 ? cbRedIcon : cbGreenIcon} alt="CB Icon" /> */}
                  {Math.abs(formattedValue)?.toFixed(6)}
                </div>
                {positiveCBWantsToSellCount > 1 && filterType === "all" ? (
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => {
                      setHasEdited(true);
                      setEditingRow(record.token_id);
                    }}
                  />
                ) : null}
              </>
            )}
          </div>
        );
      },
    },
    {
      title: <>Remaining <br /> (CB)</>,
      dataIndex: 'total_value',
      key: "remaining_cb",
      align: "center",
      render: (_, record) => {
        const rawCB = parseFloat(record.cb_wants_to_sell);
        const rawContribution = parseFloat(contributionMap[record.token_id] ?? 0);

        // Scale both down
        const cb = rawCB / 10 ** 6;
        const contribution = rawContribution / 10 ** 6;

        // Subtract and truncate precision
        const remaining = parseFloat(
          (Math.trunc((cb - contribution) * 10 ** 6) / 10 ** 6).toFixed(6)
        );

        return (
          <div className="vessel-value" style={{ color: remaining < 0 ? "#FB2F2F" : "#0CC876" }}>
            {/* <img width={25} src={remaining < 0 ? cbRedIcon : cbGreenIcon} alt="CB Icon" /> */}
            {Math.abs(remaining).toFixed(6)}
          </div>
        );
      },
    },
    {
      title: <>Final Price <br /> (€)</>,
      dataIndex: 'total_value',
      key: 'total_value',
      align: "center",
      render: (value) => (
        <div className="vessel-value" style={{ color: parseFloat(value) < 0 ? "#678DFF" : "#678DFF" }}>
          {/* <img width={25} src={parseFloat(value) < 0 ? cbRedIcon : cbGreenIcon} alt="Final Price Icon" /> */}
          {addThousandSeparator((value / (10 ** 6)))}
        </div>
      ),
    },
    {
      title: 'Ownership',
      dataIndex: 'member_type',
      key: 'member_type',
      align: "center",
      render: (member_type) => (
        member_type == "host" ? "Owned By You" : ""
      ),
    },
  ];

  const data = filterVessels(filterType, removeLastDictionary(AutoPoolsData));

  const positiveCBWantsToSellCount = data.filter(item => item.cb_wants_to_sell > 0).length;

  const pool_closed_At = Math.abs(
    data
      .filter(item => item.cb_wants_to_sell < 0)
      .reduce((sum, item) => sum + item.cb_wants_to_sell, 0)
  );

  const price_to_pay = data.reduce((sum, item) => sum + item.total_value, 0);

  const totalContribution = data
    .filter(item => item.cb_consumed !== null)
    .reduce((sum, item) => sum + parseFloat(item.cb_consumed || 0), 0);

  const totalDeficit = Math.abs(
    data
      .filter(item => item.cb_wants_to_sell < 0)
      .reduce((sum, item) => sum + parseFloat(item.cb_wants_to_sell || 0), 0)
  );

  const contributionOffset = ((totalContribution - totalDeficit) / 10 ** 6).toFixed(4);
  const [mynewdata, setmynewdata] = useState({});
  useEffect(() => {
    if (contributionOffset == 0) {
      dispatch(updatePriceBreakDown(mynewdata));
    }
  }, [contributionOffset, hasEdited])

  const checkContributionForPool = () => {
    let updatedData = data.map(item => {
      return {
        ...item,
        cb_consumed: item.cb_wants_to_sell >= 0 ? parseFloat(contributionMap[item.token_id]) : null,
      };
    });

    const totalContribution = updatedData
      .filter(item => item.cb_consumed !== null)
      .reduce((sum, item) => sum + item.cb_consumed, 0);

    const totalDeficit = Math.abs(
      data
        .filter(item => item.cb_wants_to_sell < 0)
        .reduce((sum, item) => sum + item.cb_wants_to_sell, 0)
    );
    if ((totalContribution / 10 ** 6).toFixed(4) > (totalDeficit / 10 ** 6).toFixed(4)) {
      notification.warning({
        message: "Insufficient Contribution",
        description: "Total contribution is more than required for the pool.",
        icon: <AlignLeftOutlined style={{ color: "#faad14" }} />,
        placement: "topRight",
      });

    }
    else if ((totalContribution / 10 ** 6).toFixed(4) < (totalDeficit / 10 ** 6).toFixed(4)) {
      notification.warning({
        message: "Insufficient Contribution",
        description: "Total contribution is less than required for the pool.",
        icon: <AlignLeftOutlined style={{ color: "#faad14" }} />,
        placement: "topRight",
      });

    }
    else if ((totalContribution / 10 ** 6).toFixed(4) == (totalDeficit / 10 ** 6).toFixed(4)) {
      setHasEdited(false);

    }

    const lastDictionary = getLastDictionary(AutoPoolsData);
    const finalData = [...updatedData, lastDictionary];

    dispatch(updateAutoPoolsData(finalData));
    updatedData = updatedData.map(item => ({
      ...item,
      // total_value: lastDictionary.total_surplus_value
    }));

    const priceBreakDownPayLoad = {
      data: updatedData,
      total_surplus_value: lastDictionary.total_surplus_value,
      pool_id: "",
    }

    setmynewdata(priceBreakDownPayLoad);
    // dispatch(generateAutoPoolData(AutoPoolsDataPayload));
  };

  const handleOpenModal = () => {
    dispatch(getFinalPoolDetails(removeLastDictionary(AutoPoolsData)));
    setFinalModalVisible(true);

  }


  return (
    <Modal
      title={
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", paddingRight: "1.2vw" }}>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-start", paddingRight: "1.2vw" }}>
            <span>Auto-Pooling</span>
            <div style={{ marginBottom: 16, fontWeight: 500 }}>This system generates 5 pools tailored to meet the specific requirements of the CB.</div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", paddingRight: "1.2vw" }}>
            <div
              className="contribution-difference-status-box"
              style={{
                backgroundColor:
                  contributionOffset > 0
                    ? "#0CC87633"
                    : "#FB2F2F33",
                padding: "4px 8px",
                borderRadius: 4,
              }}
            >
              <span style={{ fontSize: "15px", display: "block", fontWeight: "bold" }}>Contribution Offset</span>
              <span
                style={{
                  color:
                    contributionOffset > 0
                      ? "#0CC876"
                      : "#FB2F2F",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <img
                  style={{ width: "3vh", display: "inline-block" }}
                  src={
                    contributionOffset > 0
                      ? cbGreenIcon
                      : cbRedIcon
                  }
                  alt="cb icon"
                />
                {contributionOffset} CB
              </span>
            </div>
          </div>
        </div>
      }
      visible={isModalVisible}
      onCancel={handleCancel}
      footer={null}
      width={1200}
      className="generated-pool-modal full-screen-modal"
      closeIcon={<CloseOutlined style={{ fontSize: '20px', fontWeight: 'bold', color: 'red', maxHeight: "100vh" }} />}

    >
      {AutoPoolsDataPayload.length != 0 ? <Tabs activeKey={`${activePoolIndex + 1}`} onChange={handleTabChange}>
        {Array.isArray(AutoPoolsDataPayload) && AutoPoolsDataPayload.map((pool, index) => (
          <Tabs.TabPane className='generated-pool' tab={`Pool ${index + 1}`} key={`${index + 1}`}>
            <div className='generated-header'>
              <div className="generated-filter-buttons">
                <Button className={`generated-filter-button ${filterType == "all" ? "active" : ""}`} onClick={() => handleFilterChange('all')}>All Vessels</Button>
                <Button className={`generated-filter-button surplus-button ${filterType == "surplus" ? "active" : ""}`} onClick={() => handleFilterChange('surplus')}>Surplus Vessels</Button>
                <Button className={`generated-filter-button deficit-button ${filterType == "deficit" ? "active" : ""}`} onClick={() => handleFilterChange('deficit')}>Deficit Vessels</Button>
              </div>
              <div className="generated-price-details">
                <div className="generated-status-box" style={{ backgroundColor: getLastDictionary(AutoPoolsData)?.total_surplus_cb / (10 ** 6) > 0 ? "#0CC87633" : "#FB2F2F33" }}>
                  <span style={{ fontSize: "15px" }}>Total Surplus CB</span>
                  <span style={{ color: getLastDictionary(AutoPoolsData)?.total_surplus_cb > 0 ? "#0CC876" : "#FB2F2F" }}><img style={{ width: '3vh', justifyContent: 'center' }} src={getLastDictionary(AutoPoolsData)?.total_surplus_cb / (10 ** 6) > 0 ? cbGreenIcon : cbRedIcon} alt="cb icon" />{Math.abs(getLastDictionary(AutoPoolsData)?.total_surplus_cb / (10 ** 6)).toFixed(6)} CB</span>
                </div>
                <div className="generated-status-box" style={{ backgroundColor: getLastDictionary(AutoPoolsData)?.total_deficit_cb / (10 ** 6) > 0 ? "#0CC87633" : "#FB2F2F33" }}>
                  <span style={{ fontSize: "15px" }}>Total Deficit CB</span>
                  <span style={{ color: getLastDictionary(AutoPoolsData)?.total_deficit_cb > 0 ? "#0CC876" : "#FB2F2F" }}><img style={{ width: '3vh', justifyContent: 'center' }} src={getLastDictionary(AutoPoolsData)?.total_deficit_cb / (10 ** 6) > 0 ? cbGreenIcon : cbRedIcon} alt="cb icon" />{Math.abs(getLastDictionary(AutoPoolsData)?.total_deficit_cb / (10 ** 6)).toFixed(6)} CB</span>
                </div>
                <div className="generated-status-box" style={{ backgroundColor: getLastDictionary(AutoPoolsData)?.remaining_cb / (10 ** 6) > 0 ? "#0CC87633" : "#FB2F2F33" }}>
                  <span style={{ fontSize: "15px" }}>Remaining CB</span>
                  <span style={{ color: getLastDictionary(AutoPoolsData)?.remaining_cb > 0 ? "#0CC876" : "#FB2F2F" }}><img style={{ width: '3vh', justifyContent: 'center' }} src={getLastDictionary(AutoPoolsData)?.remaining_cb / (10 ** 6) > 0 ? cbGreenIcon : cbRedIcon} alt="cb icon" />{Math.abs(getLastDictionary(AutoPoolsData)?.remaining_cb / (10 ** 6)).toFixed(6)} CB</span>
                </div>
                {/* <div className="generated-status-box" style={{backgroundColor:getLastDictionary(AutoPoolsData)?.price > 0 ? "#678DFF33" :"#678DFF33"}}>
                <span style={{fontSize:"15px"}}>Price  to Pay</span>
                <span style={{color:getLastDictionary(AutoPoolsData)?.price > 0 ? "#678DFF" :"#678DFF"}}>€ {Math.abs(getLastDictionary(AutoPoolsData)?.price).toFixed(2)}</span>
                </div> */}
              </div>
            </div>
            <Table className="generated-vessels-table" columns={columns} pagination={false} style={{ width: "100vw"}} scroll={{ y: scrollHeight }} bordered dataSource={data} rowKey="token_id" />
            <div
              className='generated-pool-footer'
            >
              <div className='footer-title'><img width={14} src={infoIcon} />To continue with this pool, {!hasEdited ? "select Finalize & Proceed." : "select Check Contribution & Proceed to check the offset of Distribution at top right corner"}</div>
              {!hasEdited ? <Button className='finalize-button' onClick={handleOpenModal}>{`Finalize & Proceed`}<img style={{ marginLeft: '0.5vw' }} src={vectorrightIcon} /><img src={vectorrightIcon} /></Button>
                : <Button className='finalize-button' onClick={checkContributionForPool}>{`Check Contribution & Proceed`}</Button>}

            </div>
          </Tabs.TabPane>
        ))}
      </Tabs>

        : <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh", fontSize: "24px", fontWeight: "500", textAlign: "center" }}>
          Pool was not created as no suitable matches were found.
        </div>}

      <ConfirmationModal
        isVisible={isFinalModalVisible}
        onCancel={handleCloseModal}
        onConfirm={handleConfirm}
        poolClosedAt={pool_closed_At}
        priceToPay={price_to_pay}
      />
    </Modal>
  );
};

export default GeneratedPools;